import { LngLatBoundsLike } from 'mapbox-gl';

import FireBan from '@/common/assets/icons/fire_ban.png';
import ExtremeOrCatastrophicFDR from '@/common/assets/icons/hazards/ew-extreme-or-catastrophic-fdr.svg?url';
import Pin from '@/common/assets/icons/pin.png';
import { CentreSubType, ClosureSubType, EntitySubType, EventSubType, FdrSubType, IncidentSubType, WarningSubTypes } from './domain';

// Incidents
import { CENTRE_HAZARD_ICONS, CLOSURE_HAZARD_ICONS, FDR_HAZARD_ICONS, INCIDENT_HAZARD_ICONS, WARNING_HAZARD_ICONS } from './icons';

// TODO: separate the environment styles, these are currently hardcoded prod styles.
export const MAPBOX_STYLE = 'mapbox://styles/genvis-manitoba/cmbeksmh9004601sq63b4aj5n'; // new style without LGA etc.
export const MAPBOX_SATELLITE_STYLE = 'mapbox://styles/genvis-manitoba/cmbelc8oz003y01sn45m7evlx';
export const MANITOBA_BBOX: LngLatBoundsLike = [-106.0, 48.0, -88.0, 60.3];
// This is the centre of the MANITOBA_BBOX bounding box. It can be used for geocoding searches.
// export const MANITOBA_BBOX_CENTRE = [-98.3786844, 51];

export const HAZARD_MARKERS = {
  ...INCIDENT_HAZARD_ICONS,
  ...WARNING_HAZARD_ICONS,
  ...CENTRE_HAZARD_ICONS,
  ...CLOSURE_HAZARD_ICONS,
  ...FDR_HAZARD_ICONS,
  'ew-fire-ban': FireBan,
  'ew-extreme-or-catastrophic-fdr': ExtremeOrCatastrophicFDR,
};

export const PIN_IMAGE = {
  id: 'pin',
  url: Pin,
};

export const TOTAL_FIRE_BAN_MARKER = {
  id: 'total-fire-ban',
  url: FireBan,
};

// Sources
export const enum MapboxSource {
  addressPinSource = 'address-pin-source',
  osmSource = 'osm-source',
  satelliteSource = 'satellite-source',

  hazardsMarkersSource = 'hazards-markers-source',
  hazardsPolygonsSource = 'hazards-polygons-source',
  hazardsFireShapePolygonsSource = 'hazards-fire-shape-polygons-source',
  hazardsPolylinesSource = 'hazards-polylines-source',

  // Overlays tileset source
  overlaysSource = 'overlays-source',

  // BOM sources
  cycloneTrackSource = 'cyclone-track-source',
  rainRadarSource = 'rain-radar-source',
  weatherSatelliteSource = 'weather-satellite-source',
  thunderstormTrackerSource = 'thunderstorm-tracker-source',
  windDirectionSource = 'wind-direction-source',
  floodCatchmentSource = 'flood-catchment-source',
}

// Layers
export const enum MapboxLayer {
  invisibleLayer = 'invisible-layer',
  addressPinLayer = 'address-pin-layer',
  osmLayer = 'osm-layer',

  hazardsPolygonsLayer = 'hazard-polygons-layer',
  hazardsPolygonsStrokeLayer = 'hazard-polygons-stroke-layer',
  hazardsFireShapePolygonsLayer = 'hazard-fire-shape-polygons-layer',
  hazardsFireShapePolygonsStrokeLayer = 'hazard-fire-shape-polygons-stroke-layer',
  hazardsPolylinesStrokeLayer = 'hazard-polylines-stroke-layer',

  // Overlays layers
  dfesRegionsLayer = 'dfes-regions-layer',
  lgaLayer = 'lga-layer',
  pastoralLayer = 'pastoral-layer',

  // Bom layers
  cycloneTrackLayer = 'cyclone-track-layer',
  rainRadarLayer = 'rain-radar-layer',
  weatherSatelliteLayer = 'weather-satellite-layer',
  thunderstormTrackerLayer = 'thunderstorm-tracker-layer',
  windDirectionLayer = 'wind-direction-layer',
  floodCatchmentLayer = 'flood-catchment-layer',
}

export const enum MapboxLabelLayer {
  dfesRegionsLayer = 'dfes-region-label-layer',
  lgaLayer = 'lga-label-layer',
  pastoralLayer = 'pastoral-label-layer',
}

// Styles
export const enum MapboxStyle {
  default = 'default',
  satellite = 'satellite',
}

// Pollers
export const enum Poller {
  fetchAllHazards = 'fetch-all-hazards',
}

export const HazardPriorityMapping: Record<(typeof EntitySubType)[keyof typeof EntitySubType], number> = {
  // Warnings
  [WarningSubTypes.BushfireEmergencyWarningWarningEntity]: 6.99,
  [WarningSubTypes.CycloneEmergencyWarningWarningEntity]: 6.98,
  [WarningSubTypes.FloodEmergencyWarningWarningEntity]: 6.97,
  [WarningSubTypes.StormEmergencyWarningWarningEntity]: 6.96,
  [WarningSubTypes.StormSurgeEmergencyWarningWarningEntity]: 6.95,
  [WarningSubTypes.CoastalHazardEmergencyWarningWarningEntity]: 6.94,
  [WarningSubTypes.DamagingWindsEmergencyWarningWarningEntity]: 6.93,
  [WarningSubTypes.HeatwaveEmergencyWarningWarningEntity]: 6.92,
  [FdrSubType.CatastrophicFdrEntity]: 6.91,
  [WarningSubTypes.HazmatGeneralWarningWarningEntity]: 6.9,
  [WarningSubTypes.TsunamiLandWarningWarningEntity]: 6.89,
  [WarningSubTypes.TsunamiMarineWarningWarningEntity]: 6.88,

  [WarningSubTypes.BushfireWatchAndActWarningEntity]: 5.99,
  [WarningSubTypes.CycloneWatchAndActWarningEntity]: 5.98,
  [WarningSubTypes.FloodWatchAndActWarningEntity]: 5.97,
  [WarningSubTypes.StormWatchAndActWarningEntity]: 5.96,
  [WarningSubTypes.StormSurgeWatchAndActWarningEntity]: 5.95,
  [WarningSubTypes.CoastalHazardWatchAndActWarningEntity]: 5.94,
  [WarningSubTypes.DamagingWindsWatchAndActWarningEntity]: 5.93,
  [WarningSubTypes.HeatwaveWatchAndActWarningEntity]: 5.92,
  [FdrSubType.ExtremeFdrEntity]: 5.91,
  [WarningSubTypes.TsunamiWatchWarningEntity]: 5.9,

  [WarningSubTypes.BushfireAdviceWarningEntity]: 4.99,
  [WarningSubTypes.CycloneAdviceWarningEntity]: 4.98,
  [WarningSubTypes.FloodAdviceWarningEntity]: 4.97,
  [WarningSubTypes.StormAdviceWarningEntity]: 4.96,
  [WarningSubTypes.StormSurgeAdviceWarningEntity]: 4.95,
  [WarningSubTypes.CoastalHazardAdviceWarningEntity]: 4.94,
  [WarningSubTypes.DamagingWindsAdviceWarningEntity]: 4.93,
  [WarningSubTypes.HeatwaveAdviceWarningEntity]: 4.92,
  [FdrSubType.HighFdrEntity]: 4.91,
  [WarningSubTypes.EarthquakeAlertWarningEntity]: 4.9,
  [WarningSubTypes.AdviceWarningEntity]: 4.89,
  [WarningSubTypes.CommunityInformationWarningEntity]: 4.88,
  [WarningSubTypes.SmokeAlertWarningEntity]: 4.87,
  [WarningSubTypes.StructureFireAdviceWarningEntity]: 4.86,
  [WarningSubTypes.TotalFireBanWarningEntity]: 4.85,

  [WarningSubTypes.EarthquakeFinalAlertWarningEntity]: 3.97,
  [WarningSubTypes.HazmatControlledContainedWarningEntity]: 3.95,
  [WarningSubTypes.TsunamiAllClearWarningEntity]: 3.93,
  [WarningSubTypes.StructureFireAllClearWarningEntity]: 3.92,
  [CentreSubType.EvacuationCentreEntity]: 3.9,
  [CentreSubType.EvacuationRespiteCentreEntity]: 3.91,
  [CentreSubType.GenericCentreEntity]: 3.905,
  [FdrSubType.ModerateFdrEntity]: 3.89,

  [WarningSubTypes.OtherWarningWarningEntity]: 0,
  [WarningSubTypes.AlertWarningEntity]: 0,
  [WarningSubTypes.WarningStateWideWarningEntity]: 0,
  [WarningSubTypes.WarningLocationSpecificWarningEntity]: 0,
  [FdrSubType.NoRatingFdrEntity]: 0,

  [IncidentSubType.BushfireIncidentEntity]: -1,
  [IncidentSubType.HazardousMaterialsIncidentEntity]: -1,
  [IncidentSubType.CoastalHazardIncidentEntity]: -1,
  [IncidentSubType.DamagingWindsIncidentEntity]: -1,
  [IncidentSubType.StormSurgeIncidentEntity]: -1,
  [IncidentSubType.OtherIncidentEntity]: -1,
  [IncidentSubType.StormIncidentEntity]: -1,
  [IncidentSubType.StructureFireIncidentEntity]: -1,
  [IncidentSubType.TsunamiIncidentEntity]: -1,
  [IncidentSubType.CycloneIncidentEntity]: -1,
  [IncidentSubType.EarthquakeIncidentEntity]: -1,
  [IncidentSubType.FloodIncidentEntity]: -1,
  [IncidentSubType.HumanPandemicIncidentEntity]: -1,
  [IncidentSubType.HumanEpidemicIncidentEntity]: -1,
  [IncidentSubType.RescueIncidentEntity]: -1,
  [IncidentSubType.StormDamageIncidentEntity]: -1,
  [ClosureSubType.SchoolClosureEntity]: -1,
  [ClosureSubType.RoadClosureEntity]: -1,
  [ClosureSubType.FacilityOrParkClosureEntity]: -1,
  [ClosureSubType.ParkClosureEntity]: -1,
  [ClosureSubType.GenericClosureEntity]: -1,
  [IncidentSubType.RoadIncidentEntity]: -1,
  [IncidentSubType.RoadWorkEntity]: -1,
  [IncidentSubType.BlizzardIncidentEntity]: -1,
  [IncidentSubType.TornadoIncidentEntity]: -1,

  [WarningSubTypes.PowerOutageWarningEntity]: -2,
  [WarningSubTypes.NoTotalFireBanWarningEntity]: -2,

  // MB-specific warning subtypes
  [WarningSubTypes.AirQualityEmergencyWarningEntity]: 4.85,
  [WarningSubTypes.AirQualityWatchAndActWarningEntity]: 3.85,
  [WarningSubTypes.AirQualityAdviceWarningEntity]: 2.85,
  [WarningSubTypes.BlizzardEmergencyWarningEntity]: 4.84,
  [WarningSubTypes.BlizzardWatchAndActWarningEntity]: 3.84,
  [WarningSubTypes.BlizzardAdviceWarningEntity]: 2.84,
  [WarningSubTypes.ExtremeColdEmergencyWarningEntity]: 4.83,
  [WarningSubTypes.ExtremeColdWatchAndActWarningEntity]: 3.83,
  [WarningSubTypes.ExtremeColdAdviceWarningEntity]: 2.83,
  [WarningSubTypes.FlashFloodEmergencyWarningEntity]: 4.82,
  [WarningSubTypes.FlashFloodWatchAndActWarningEntity]: 3.82,
  [WarningSubTypes.FlashFloodAdviceWarningEntity]: 2.82,
  [WarningSubTypes.FreezingRainEmergencyWarningEntity]: 4.81,
  [WarningSubTypes.FreezingRainWatchAndActWarningEntity]: 3.81,
  [WarningSubTypes.FreezingRainAdviceWarningEntity]: 2.81,
  [WarningSubTypes.IceJamEmergencyWarningEntity]: 4.8,
  [WarningSubTypes.IceJamWatchAndActWarningEntity]: 3.8,
  [WarningSubTypes.IceJamAdviceWarningEntity]: 2.8,
  [WarningSubTypes.LakeFloodingEmergencyWarningEntity]: 4.79,
  [WarningSubTypes.LakeFloodingWatchAndActWarningEntity]: 3.79,
  [WarningSubTypes.LakeFloodingAdviceWarningEntity]: 2.79,
  [WarningSubTypes.PowerOutageEmergencyWarningEntity]: 3.78,
  [WarningSubTypes.PowerOutageWatchAndActWarningEntity]: 2.78,
  [WarningSubTypes.RiverFloodingEmergencyWarningEntity]: 4.78,
  [WarningSubTypes.RiverFloodingWatchAndActWarningEntity]: 3.77,
  [WarningSubTypes.RiverFloodingAdviceWarningEntity]: 2.77,
  [WarningSubTypes.SeasonalOverlandFloodingEmergencyWarningEntity]: 4.77,
  [WarningSubTypes.SeasonalOverlandFloodingWatchAndActWarningEntity]: 3.76,
  [WarningSubTypes.SeasonalOverlandFloodingAdviceWarningEntity]: 2.76,
  [WarningSubTypes.SevereWinterWeatherEmergencyWarningEntity]: 4.76,
  [WarningSubTypes.SevereWinterWeatherWatchAndActWarningEntity]: 3.75,
  [WarningSubTypes.SevereWinterWeatherAdviceWarningEntity]: 2.75,
  [WarningSubTypes.TornadoEmergencyWarningEntity]: 4.75,
  [WarningSubTypes.TornadoWatchAndActWarningEntity]: 3.74,
  [WarningSubTypes.TornadoAdviceWarningEntity]: 2.74,
  [WarningSubTypes.OtherEmergencyWarningWarningEntity]: 0,
  [WarningSubTypes.OtherWatchAndActWarningEntity]: 0,

  // Events - These do not need to be displayed on the map
  [EventSubType.BushfireEventEntity]: -3,
  [EventSubType.HazardousMaterialsEventEntity]: -3,
  [EventSubType.OtherEventEntity]: -3,
  [EventSubType.StormEventEntity]: -3,
  [EventSubType.StructureFireEventEntity]: -3,
  [EventSubType.TsunamiEventEntity]: -3,
  [EventSubType.CycloneEventEntity]: -3,
  [EventSubType.EarthquakeEventEntity]: -3,
  [EventSubType.FloodEventEntity]: -3,
  [EventSubType.HumanPandemicEventEntity]: -3,
  [EventSubType.HumanEpidemicEventEntity]: -3,
  [EventSubType.RescueEventEntity]: -3,
  [EventSubType.StormDamageEventEntity]: -3,
  [EventSubType.SmokeAlertEventEntity]: -3,
  [EventSubType.TotalFireBanEventEntity]: -3,
  [EventSubType.HeatwaveEventEntity]: -3,
  [EventSubType.PandemicEventEntity]: -3,
  [EventSubType.SchoolClosureEventEntity]: -3,
  [EventSubType.PowerOutageEventEntity]: -3,
  [EventSubType.EvacuationCentreEventEntity]: 0,
  [EventSubType.OtherDBCAEventEntity]: 0,
  [EventSubType.OtherDFESEventEntity]: 0,
  [EventSubType.AirQualityEventEntity]: 0,
  [EventSubType.BlizzardEventEntity]: 0,
  [EventSubType.TornadoEventEntity]: 0,
  [EventSubType.WinterWeatherEventEntity]: 0,
};
